import React from 'react';
import { Calendar, Clock, User, Star, CheckCircle, XCircle, ArrowRight, Lightbulb, TrendingUp, Database, Search, Zap, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const TestDoctrinePage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' 
      ? "Doctrine : Test Complet de la Legaltech Française de Recherche Juridique"
      : "Doctrine: Complete Review of the French Legal Research Platform",
    "datePublished": "2026-01-16T10:00:00+01:00",
    "author": { "@type": "Organization", "name": "Gaius Legal AI" }
  };

  const seoTitle = language === 'fr' 
    ? "Doctrine Avocat 2026 : Test Complet (Avis, Prix, Comparatif)"
    : "Doctrine 2026: Complete Lawyer Review";

  const seoDescription = language === 'fr'
    ? "Test approfondi de Doctrine, la legaltech française de référence pour la recherche juridique. Fonctionnalités IA, jurisprudence française, comparatif avec Lexis Nexis IA."
    : "In-depth review of Doctrine, the leading French legaltech for legal research. AI features, French case law, comparison with Lexis Nexis AI.";

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title={seoTitle}
          description={seoDescription}
          keywords="doctrine legaltech, doctrine avocat, recherche juridique ia, jurisprudence française, doctrine vs lexis nexis, ia générative avocats, base de données juridique"
          canonicalUrl="https://gaius-tech.com/articles/test-doctrine"
          alternateUrlFr="https://gaius-tech.com/articles/test-doctrine"
          alternateUrlEn="https://gaius-tech.com/articles/test-doctrine"
          structuredData={structuredData}
          pageType="article"
        />
        
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Doctrine pour Avocats : Test Complet 2026 de la Legaltech Française de Recherche Juridique IA
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Test complet de la legaltech qui a transformé l'accès à la jurisprudence française. Fonctionnalités IA, comparatif et verdict.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>16 janvier 2026</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>15 min de lecture</span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Équipe Gaius</span>
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=400&fit=crop"
              alt="Doctrine - recherche juridique IA"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-xl leading-relaxed mb-8">
              Doctrine a révolutionné l'accès à la jurisprudence française. En rendant des millions de décisions accessibles et recherchables de manière intelligente, cette legaltech française s'est imposée comme un outil incontournable pour les praticiens du droit. Nous avons testé ses fonctionnalités IA pour vous livrer une analyse complète.
            </p>

            <div className="bg-primary/5 border-l-4 border-primary p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-bold text-lg mb-2">Doctrine en chiffres</h4>
                  <ul className="text-foreground/80 space-y-1 mb-0">
                    <li>• Plus de 10 millions de décisions indexées</li>
                    <li>• 50 000+ utilisateurs professionnels</li>
                    <li>• Couverture : toutes juridictions françaises</li>
                    <li>• Mise à jour quotidienne de la base</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Qu'est-ce que Doctrine ? Histoire de la legaltech juridique française
            </h2>

            <p className="mb-6">
              Fondée en 2016, Doctrine est née d'un constat simple : l'accès à la jurisprudence française était fragmenté, coûteux et inefficace. Là où les praticiens devaient jongler entre Légifrance (gratuit mais limité), les bases privées (chères) et les recherches manuelles, Doctrine a proposé une solution unifiée exploitant l'intelligence artificielle.
            </p>

            <p className="mb-6">
              L'ambition initiale : indexer l'intégralité de la jurisprudence française et la rendre accessible via une recherche sémantique. Aujourd'hui, Doctrine va bien au-delà avec des fonctionnalités d'<strong>IA générative</strong> qui transforment la recherche juridique.
            </p>

            <p className="mb-8">
              La startup a levé plus de 30 millions d'euros et compte parmi ses investisseurs des acteurs majeurs du capital-risque européen. Cette solidité financière garantit la pérennité et le développement continu de la plateforme.
            </p>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Test Doctrine : fonctionnalités clés pour avocats et juristes
            </h2>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              1. La base jurisprudentielle la plus complète de France
            </h3>

            <p className="mb-6">
              Doctrine indexe les décisions de toutes les juridictions françaises : Cour de cassation, Conseil d'État, cours d'appel, tribunaux judiciaires, tribunaux administratifs, juridictions spécialisées. L'exhaustivité est impressionnante.
            </p>

            <p className="mb-8">
              Ce qui distingue Doctrine de Légifrance, c'est l'enrichissement des données : chaque décision est structurée, les références croisées sont identifiées, les textes applicables sont liés, les évolutions jurisprudentielles sont tracées.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              2. Recherche sémantique avancée
            </h3>

            <p className="mb-6">
              Oubliez les recherches par mots-clés. Doctrine comprend le sens de vos requêtes et identifie les décisions pertinentes même si elles utilisent une terminologie différente. La recherche sémantique fait toute la différence.
            </p>

            <p className="mb-8">
              <strong>Exemple concret :</strong> Une recherche sur « responsabilité du constructeur pour défaut d'étanchéité » identifiera automatiquement les décisions parlant de « vice de construction », « infiltrations », « défaut d'imperméabilisation » ou « malfaçons affectant le clos et couvert ».
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              3. Analyse automatique des décisions
            </h3>

            <p className="mb-6">
              Doctrine extrait automatiquement les informations clés de chaque décision : faits, procédure, moyens, solution, principe dégagé. Cette structuration facilite considérablement l'analyse et la synthèse.
            </p>

            <p className="mb-8">
              Les « fiches arrêts » générées automatiquement constituent un gain de temps précieux pour les collaborateurs en charge de la recherche.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              4. Veille juridique intelligente
            </h3>

            <p className="mb-6">
              Configurez des alertes sur vos thématiques et recevez les nouvelles décisions pertinentes dès leur publication. La veille est personnalisable par domaine de droit, juridiction, type de contentieux.
            </p>

            <p className="mb-8">
              Cette fonctionnalité est particulièrement appréciée des avocats spécialisés qui doivent rester à la pointe de l'actualité jurisprudentielle dans leur domaine.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              5. Fonctionnalités IA générative (nouveau)
            </h3>

            <p className="mb-6">
              Doctrine a récemment intégré des capacités d'<strong>IA générative</strong> permettant d'obtenir des synthèses automatiques, des analyses comparatives et des réponses en langage naturel.
            </p>

            <p className="mb-8">
              Ces fonctionnalités complètent la recherche traditionnelle sans la remplacer : Doctrine reste avant tout une base de données enrichie par l'IA, pas une IA générative pure comme Harvey ou Jimini.
            </p>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Doctrine vs Lexis Nexis IA : comparatif pour avocats 2026
            </h2>

            <div className="bg-secondary/50 rounded-xl p-8 my-10">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-lg p-6">
                  <h4 className="font-bold mb-4 flex items-center">
                    <Database className="w-5 h-5 text-primary mr-2" />
                    Doctrine
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Spécialiste jurisprudence française</li>
                    <li>✓ Interface moderne et intuitive</li>
                    <li>✓ Tarification transparente</li>
                    <li>✓ Recherche sémantique native</li>
                    <li>✗ Pas de doctrine (ironiquement)</li>
                    <li>✗ IA générative en développement</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-6">
                  <h4 className="font-bold mb-4 flex items-center">
                    <BookOpen className="w-5 h-5 text-primary mr-2" />
                    Lexis Nexis (Lexis+ AI)
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Jurisprudence + doctrine + encyclopédies</li>
                    <li>✓ Couverture internationale</li>
                    <li>✓ IA générative intégrée</li>
                    <li>✗ Interface vieillissante</li>
                    <li>✗ Tarification complexe</li>
                    <li>✗ Courbe d'apprentissage</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Avis avocats : les points forts de Doctrine
            </h2>

            <div className="space-y-6 my-8">
              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Exhaustivité de la base jurisprudentielle</h4>
                  <p className="text-muted-foreground mt-1">
                    La couverture des décisions françaises est inégalée. Doctrine a fait un travail remarquable d'indexation et de structuration.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Recherche sémantique exceptionnelle</h4>
                  <p className="text-muted-foreground mt-1">
                    La compréhension du sens dépasse largement la simple correspondance de mots-clés. C'est un vrai plus par rapport aux outils traditionnels.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Interface utilisateur moderne</h4>
                  <p className="text-muted-foreground mt-1">
                    L'ergonomie de Doctrine est exemplaire. La prise en main est intuitive, même pour les utilisateurs peu technophiles.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Veille intelligente</h4>
                  <p className="text-muted-foreground mt-1">
                    Le système d'alertes est précis et pertinent. Plus de bruit inutile, uniquement les décisions qui comptent.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Limites de Doctrine : ce qu'il faut savoir avant de s'abonner
            </h2>

            <div className="space-y-6 my-8">
              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Pas de doctrine (au sens strict)</h4>
                  <p className="text-muted-foreground mt-1">
                    Malgré son nom, Doctrine ne propose pas d'accès aux encyclopédies, traités et commentaires doctrinaux. Pour cela, Dalloz ou LexisNexis restent nécessaires.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Focus exclusivement français</h4>
                  <p className="text-muted-foreground mt-1">
                    Pas de couverture internationale. Pour les dossiers cross-border, il faut combiner avec d'autres sources.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">IA générative encore limitée</h4>
                  <p className="text-muted-foreground mt-1">
                    Les fonctionnalités d'IA générative sont moins avancées que chez les pure players comme Harvey ou Jimini. Doctrine reste avant tout une base de données.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <Lightbulb className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-bold text-lg mb-2">Notre recommandation</h4>
                  <p className="text-amber-700 mb-0">
                    Doctrine est indispensable pour la recherche jurisprudentielle française. Combinez-le avec un outil d'IA générative (Jimini AI, Harvey) pour couvrir l'ensemble de vos besoins : recherche exhaustive + analyse et rédaction augmentées.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Notre verdict Doctrine 2026 : note 5/5 pour la recherche juridique IA
            </h2>

            <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
              ))}
              <span className="ml-4 text-2xl font-bold">5/5</span>
            </div>

            <p className="mb-6">
              Doctrine mérite sa note maximale pour avoir révolutionné l'accès à la jurisprudence française. L'exhaustivité de la base, la qualité de la recherche sémantique et l'ergonomie de l'interface en font un outil incontournable.
            </p>

            <p className="mb-8">
              Pour tout praticien du droit français, Doctrine devrait faire partie de la boîte à outils de base, au même titre qu'un traitement de texte ou une messagerie. C'est devenu un standard de la profession.
            </p>

            <div className="bg-primary/5 rounded-xl p-8 my-12">
              <h3 className="text-2xl font-bold mb-6 text-foreground">Récapitulatif Doctrine</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-green-700 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" /> Points forts
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Base jurisprudentielle exhaustive</li>
                    <li>✓ Recherche sémantique exceptionnelle</li>
                    <li>✓ Interface moderne</li>
                    <li>✓ Veille intelligente</li>
                    <li>✓ Tarification accessible</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-red-700 mb-3 flex items-center">
                    <XCircle className="w-5 h-5 mr-2" /> Limitations
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✗ Pas de doctrine/commentaires</li>
                    <li>✗ Focus France uniquement</li>
                    <li>✗ IA générative limitée</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>Idéal pour :</strong> Recherche jurisprudentielle française, veille juridique, préparation de dossiers contentieux, analyse comparative de décisions.
                </p>
              </div>
            </div>

          </div>

          <div className="mt-16 pt-8 border-t">
            <h3 className="text-2xl font-bold mb-6">Formations recommandées</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Search className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Formation Recherche Juridique IA</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Maîtrisez les outils de recherche juridique augmentée par l'IA.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      Découvrir <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Zap className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Formation IA Générative Avocats</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Combinez recherche et IA générative pour une pratique augmentée.
                  </p>
                  <Button asChild variant="outline">
                    <Link to="/training">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="doctrine legaltech, french legal research, case law database, ai legal search"
        canonicalUrl="https://gaius-tech.com/articles/test-doctrine"
        alternateUrlFr="https://gaius-tech.com/articles/test-doctrine"
        alternateUrlEn="https://gaius-tech.com/articles/test-doctrine"
        structuredData={structuredData}
        pageType="article"
      />
      
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Doctrine for Lawyers: Complete 2026 Review of the French Legal Research AI Platform
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Complete review of the legaltech that transformed access to French case law. AI features, comparison and verdict.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-xl leading-relaxed mb-8">
            Doctrine revolutionized access to French case law. By making millions of decisions accessible and intelligently searchable, this French legaltech has become an essential tool for legal practitioners.
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Key Features
          </h2>

          <p className="mb-8">
            Doctrine indexes decisions from all French courts with exceptional coverage. What distinguishes it from Légifrance is data enrichment: every decision is structured, cross-references identified, applicable texts linked, jurisprudential evolutions traced.
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Our Final Verdict
          </h2>

          <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
            {[1,2,3,4,5].map(i => (
              <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            ))}
            <span className="ml-4 text-2xl font-bold">5/5</span>
          </div>

          <p className="mb-8">
            Doctrine deserves its maximum rating for revolutionizing access to French case law. The database completeness, semantic search quality and interface ergonomics make it an essential tool for any French law practitioner.
          </p>

        </div>

        <div className="mt-16 pt-8 border-t">
          <Button asChild>
            <Link to="/training">
              Discover our training programs <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};
